import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Modal,
  TextField,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useForm, Controller, useFormState } from "react-hook-form";
import { ProjectRecord } from "@/types";
import { sendDocumentForSignature, SendDocumentRequest } from "@/api/adobeApi";
import getErrorMessage from "@/utils/getErrorMessage";
import { EmailPatternValidation } from "@/validators/emailPatternValidation";

const StyledModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ModalContent = styled(Box)(() => ({
  backgroundColor: "#fff",
  borderRadius: "12px",
  padding: "32px",
  width: "100%",
  maxWidth: "520px",
  position: "relative",
  outline: "none",
  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
}));

const CloseButton = styled(IconButton)({
  position: "absolute",
  top: "16px",
  right: "16px",
  color: "#0076CE",
  border: "1px solid #0076CE",
  borderRadius: "8px",
  width: "40px",
  height: "40px",
  "&:hover": {
    backgroundColor: "rgba(0, 118, 206, 0.04)",
  },
});

const Title = styled(Typography)({
  fontSize: "24px",
  fontWeight: 600,
  color: "#1A1A1A",
  marginBottom: "16px",
  fontFamily: "Inter, sans-serif",
});

const Description = styled(Typography)({
  fontSize: "16px",
  color: "#666666",
  marginBottom: "24px",
  fontFamily: "Inter, sans-serif",
});

const CheckboxContainer = styled(Box)({
  marginBottom: "24px",
});

const StyledCheckbox = styled(Checkbox)({
  color: "#0076CE",
  "&.Mui-checked": {
    color: "#0076CE",
  },
});

const CheckboxLabel = styled(Typography)({
  fontSize: "14px",
  color: "#1A1A1A",
  fontFamily: "Inter, sans-serif",
});

const InputLabel = styled(Typography)({
  fontSize: "14px",
  fontWeight: 500,
  color: "#1A1A1A",
  marginBottom: "8px",
  fontFamily: "Inter, sans-serif",
});

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "isDisabled",
})<{ isDisabled?: boolean }>(({ theme, isDisabled }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "16px",
    fontFamily: "Inter, sans-serif",
    ...(isDisabled && {
      backgroundColor:
        theme.palette.sizingTool?.neutral?.backgroundHover || "#F5F5F5",
      "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: theme.palette.textGray1,
        color: theme.palette.textGray1,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor:
          theme.palette.border?.medium || theme.palette.textLightGray4,
      },
    }),
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.linkText,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.linkText,
    },
  },
}));

const ButtonContainer = styled(Box)({
  display: "flex",
  gap: "16px",
  width: "100%",
});

const CancelButton = styled(Button)({
  padding: "8px 16px",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  color: "#0076CE",
  border: "1px solid #0076CE",
  backgroundColor: "transparent",
  fontFamily: "Inter, sans-serif",
  flex: 1,
  "&:hover": {
    backgroundColor: "rgba(0, 118, 206, 0.04)",
  },
});

const SendButton = styled(Button)({
  padding: "8px 16px",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  color: "#fff",
  backgroundColor: "#0076CE",
  fontFamily: "Inter, sans-serif",
  flex: 1,
  "&:hover": {
    backgroundColor: "#0056a3",
  },
  "&:disabled": {
    backgroundColor: "#B3D9F2",
    color: "#fff",
  },
});

interface ConfirmProjectSignatureModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectDetails: ProjectRecord;
  allowEmailEdit?: boolean;
}

interface FormData {
  customerEmail: string;
  confirmed: boolean;
}

const ConfirmProjectSignatureModal: React.FC<
  ConfirmProjectSignatureModalProps
> = ({ open, onClose, onSuccess, projectDetails, allowEmailEdit = false }) => {
  const theme = useTheme();
  const { palette } = theme;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      customerEmail: projectDetails?.accountEmail || "",
      confirmed: false,
    },
  });

  const formState = useFormState({ control });
  const shouldShowError = formState.isSubmitted ?? true;
  const confirmed = watch("confirmed");

  useEffect(() => {
    if (projectDetails?.accountEmail) {
      reset({
        customerEmail: projectDetails.accountEmail,
        confirmed: false,
      });
    }
  }, [projectDetails?.accountEmail, reset]);

  const onSubmit = async (data: FormData) => {
    if (!projectDetails?.id) {
      setError("Project ID is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: SendDocumentRequest = {
        projectId: Number(projectDetails.id),
        signerEmail: data.customerEmail.trim(),
        agreementName: `SDCP Project ${projectDetails.id} Enrollment Agreement`,
      };

      const response = await sendDocumentForSignature(request);

      if (response.data.success) {
        handleClose();
        onSuccess?.();
      } else {
        setError(response.data.message || "Failed to send document");
      }
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset({
      customerEmail: projectDetails?.accountEmail || "",
      confirmed: false,
    });
    setError(null);
    setLoading(false);
    onClose();
  };

  const isDisabled = !isValid || !confirmed || loading;

  return (
    <StyledModal open={open} onClose={handleClose}>
      <ModalContent>
        <CloseButton onClick={handleClose}>×</CloseButton>

        <Title>Confirm Project Completion</Title>

        <Description>
          Please confirm that Project #{projectDetails?.id} is complete and
          ready for customer signature.
        </Description>

        {error && (
          <Box
            sx={{
              backgroundColor: palette.error?.light,
              border: `1px solid ${palette.error?.main || palette.errorText}`,
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "24px",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: palette.error?.main || palette.errorText,
                fontFamily: "Inter, sans-serif",
              }}
            >
              {error}
            </Typography>
          </Box>
        )}

        <CheckboxContainer>
          <Controller
            name="confirmed"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <StyledCheckbox
                    checked={field.value}
                    onChange={field.onChange}
                  />
                }
                label={
                  <CheckboxLabel>
                    I confirm all customer and system information is entered
                    correctly and the project is ready to be sent for signature
                  </CheckboxLabel>
                }
              />
            )}
          />
        </CheckboxContainer>

        <Box>
          <InputLabel>
            Customer Email{" "}
            <span style={{ color: palette.error?.main || palette.errorText }}>
              *
            </span>
          </InputLabel>
          <Controller
            name="customerEmail"
            control={control}
            rules={{
              required: "Customer email is required",
              ...EmailPatternValidation,
            }}
            render={({ field, fieldState: { error, isTouched } }) => {
              const showError = (shouldShowError || isTouched) && !!error;
              const isDisabled = !allowEmailEdit;

              return (
                <Box sx={{ position: "relative", width: "100%" }}>
                  <StyledTextField
                    {...field}
                    placeholder="Enter customer email"
                    variant="outlined"
                    type="email"
                    disabled={isDisabled}
                    error={showError}
                    isDisabled={isDisabled}
                    sx={{ marginBottom: showError ? "4px" : "24px" }}
                  />
                  {showError && !!error?.message && (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: palette.errorText,
                        paddingTop: "4px",
                        marginBottom: "24px",
                      }}
                    >
                      {error.message}
                    </Typography>
                  )}
                </Box>
              );
            }}
          />
        </Box>

        <Divider
          sx={{
            margin: 0,
            marginLeft: "-32px",
            marginRight: "-32px",
            marginTop: "12px",
            marginBottom: "12px",
          }}
        />

        <ButtonContainer>
          <CancelButton onClick={handleClose} disabled={loading}>
            Cancel
          </CancelButton>
          <SendButton onClick={handleSubmit(onSubmit)} disabled={isDisabled}>
            {loading ? "Sending..." : "Send PDF to Customer"}
          </SendButton>
        </ButtonContainer>
      </ModalContent>
    </StyledModal>
  );
};

export default ConfirmProjectSignatureModal;
